import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash-es';
import useIsomorphicLayoutEffect from '@/lib/use-iso-layout-effects';
import styles from './DoubleRangeInput.module.scss';
import i18n from '@/settings/i18n';

const t = i18n.init();

export default function DoubleRangeInput(props: {
  range: { min: number; max: number };
  values: { min?: number; max?: number };
  onChange: (data: { min: number; max: number }) => void;
  id: 'modal' | 'default';
}) {
  const { range, values, id } = props;
  const [min, setMin] = useState(values.min || range.min); // we use _min/_max because it can happen the the query string is out of range, provinding some weird UI.
  const [max, setMax] = useState(values.max || range.max);
  const onChange = useCallback(debounce(props.onChange, 300), []);

  useEffect(() => {
    // reset the min/max when the bound changes
    setMin(range.min);
    setMax(range.max);
  }, [range.min, range.max]);

  useIsomorphicLayoutEffect(() => {
    // align min/max whith layout
    const wrapper = document.getElementById(`range-wrapper-${id}`);
    const wrapperSlide = wrapper?.getElementsByTagName('span')[0];

    if (wrapper == null) return;
    wrapper.style.setProperty('--a', min.toString());
    wrapper.style.setProperty('--b', max.toString());
    wrapper.style.setProperty('--min', range.min.toString());
    wrapper.style.setProperty('--max', range.max.toString());

    const rangeDiff = range.max - range.min;
    const left = ((min - range.min) / rangeDiff) * 100;
    const right = 100 - ((max - range.min) / rangeDiff) * 100;

    wrapperSlide?.style.setProperty('left', `${left}%`);
    wrapperSlide?.style.setProperty('right', `${right}%`);
  }, [min, max]);

  return (
    <div>
      <h3 className="mb-3 text-sm font-bold uppercase text-gray-700">
        {t`Price range`}
      </h3>
      <div className="price-span grid grid-cols-2 gap-2">
        <span className="rounded bg-gray-200 py-3 px-3 text-xs text-gray-700 xl:text-sm">
          {min}
        </span>
        <span className="rounded bg-gray-200 py-3 px-3 text-xs text-gray-700 xl:text-sm">
          {max}
        </span>
      </div>
      <div id={`range-wrapper-${id}`} className={`${styles.wrap}`}>
        <input
          aria-label="minimum price range"
          className={`${styles.range} range-min`}
          type="range"
          min={range.min}
          max={range.max}
          value={min}
          step="1"
          onChange={(e) => {
            const value = Number(e.target.value);

            if (value < max) setMin(value);
            onChange({ min, max });
          }}
        />
        <input
          type="range"
          aria-label="price range"
          className={`${styles.range}`}
          min={range.min}
          max={range.max}
          value={max}
          step="1"
          onChange={(e) => {
            const value = Number(e.target.value);

            if (min < value) setMax(value);
            onChange({ min, max });
          }}
        />
        <span className={styles.line} />
      </div>
    </div>
  );
}
